import { useEffect, useRef, useState } from 'react';
import { Check, CloudOff, Loader2, Lock, PanelRight, Maximize2, Minimize2 } from 'lucide-react';
import { Badge } from '@/shared/components/ui/Badge';
import { cn } from '@/shared/utils/utils';
import { accessMeta } from '../constants';
import type { Note, SaveNotePayload } from '../types';

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

interface NoteEditorProps {
  note: Note;
  canEdit: boolean;
  focusMode: boolean;
  propertiesOpen: boolean;
  onToggleFocus: () => void;
  onToggleProperties: () => void;
  /** Persist editable fields (title/body) — sharing fields are preserved from the note. */
  onSave: (id: string, payload: SaveNotePayload) => Promise<void>;
}

/**
 * The notebook canvas. Edits the two real editable fields (title, notebookText)
 * and autosaves via PATCH after a short debounce. Sharing fields are preserved
 * untouched so autosave never alters access. Save state reflects the real
 * request lifecycle (Saving / Saved / Failed) — no fabricated conflict/offline.
 */
export function NoteEditor({
  note,
  canEdit,
  focusMode,
  propertiesOpen,
  onToggleFocus,
  onToggleProperties,
  onSave,
}: NoteEditorProps) {
  const [title, setTitle] = useState(note.title);
  const [text, setText] = useState(note.notebookText ?? '');
  const [saveState, setSaveState] = useState<SaveState>('idle');

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadedId = useRef(note.id);

  // Reload local content when switching notes; cancel any pending save.
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    loadedId.current = note.id;
    setTitle(note.title);
    setText(note.notebookText ?? '');
    setSaveState('idle');
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [note.id, note.title, note.notebookText]);

  const meta = accessMeta(note.access_control);

  const schedule = (nextTitle: string, nextText: string) => {
    if (!canEdit) return;
    setSaveState('saving');
    if (timer.current) clearTimeout(timer.current);
    const id = note.id;
    timer.current = setTimeout(async () => {
      try {
        await onSave(id, {
          title: nextTitle.trim() || 'Untitled Note',
          notebookText: nextText,
          access_control: note.access_control,
          shared_with_users: note.shared_with_users ?? [],
          shared_with_depts: note.shared_with_depts ?? [],
        });
        if (loadedId.current === id) setSaveState('saved');
      } catch {
        if (loadedId.current === id) setSaveState('error');
      }
    }, 800);
  };

  return (
    <section className="flex h-full flex-1 flex-col bg-surface">
      {/* Slim toolbar */}
      <div className="flex items-center justify-between gap-2 border-b border-outline px-6 py-2.5">
        <div className="flex items-center gap-2">
          <Badge tone={meta.tone}>{meta.label}</Badge>
          {!canEdit && (
            <span className="inline-flex items-center gap-1 text-body-sm text-on-surface-variant">
              <Lock className="h-3.5 w-3.5" /> Read-only
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <SaveIndicator state={saveState} canEdit={canEdit} />
          <div className="flex items-center gap-1">
            <button
              onClick={onToggleFocus}
              className="rounded-md p-1.5 text-on-surface-variant hover:bg-surface-variant"
              aria-label={focusMode ? 'Exit focus mode' : 'Focus mode'}
              title={focusMode ? 'Exit focus mode' : 'Focus mode'}
            >
              {focusMode ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
            {!focusMode && (
              <button
                onClick={onToggleProperties}
                className={cn(
                  'rounded-md p-1.5 hover:bg-surface-variant',
                  propertiesOpen ? 'text-primary' : 'text-on-surface-variant',
                )}
                aria-label="Toggle properties"
                aria-pressed={propertiesOpen}
                title="Properties"
              >
                <PanelRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1 overflow-y-auto">
        <div className={cn('mx-auto w-full px-8 py-8', focusMode ? 'max-w-3xl py-14' : 'max-w-3xl')}>
          <input
            value={title}
            disabled={!canEdit}
            onChange={(e) => {
              setTitle(e.target.value);
              schedule(e.target.value, text);
            }}
            placeholder="Untitled Note"
            className="w-full border-0 bg-transparent text-display-sm font-semibold text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none disabled:cursor-default"
            aria-label="Note title"
          />
          <div className="mt-4 h-px w-full bg-outline" />
          <textarea
            value={text}
            disabled={!canEdit}
            onChange={(e) => {
              setText(e.target.value);
              schedule(title, e.target.value);
            }}
            placeholder={canEdit ? 'Start writing…' : 'This note has no text content.'}
            className="mt-4 min-h-[55vh] w-full resize-none border-0 bg-transparent text-body-md leading-7 text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none disabled:cursor-default"
            style={{ lineHeight: '1.9' }}
            aria-label="Note body"
          />
        </div>
      </div>
    </section>
  );
}

function SaveIndicator({ state, canEdit }: { state: SaveState; canEdit: boolean }) {
  if (!canEdit) return null;
  if (state === 'saving')
    return (
      <span className="inline-flex items-center gap-1.5 text-body-sm text-on-surface-variant">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…
      </span>
    );
  if (state === 'saved')
    return (
      <span className="inline-flex items-center gap-1.5 text-body-sm text-success">
        <Check className="h-3.5 w-3.5" /> Saved
      </span>
    );
  if (state === 'error')
    return (
      <span className="inline-flex items-center gap-1.5 text-body-sm text-danger">
        <CloudOff className="h-3.5 w-3.5" /> Save failed
      </span>
    );
  return null;
}
